import type { IconName } from '@grafana/ui';
import type { ItemDataType, ContextItemData, UnknownItemData } from './types';
import { hash } from '../utils/hash';

export interface BaseParams {
  title?: string;
  // If true, the context item will not be shown in the context pills
  hidden?: boolean;
  // Image to display, if not selected, we fallback to icon
  img?: string;
  // Icon to display, if img is provided, icon is ignored
  icon?: IconName;
  /**
   * When true, bypasses size limits for fixed-size content that doesn't need truncation.
   *
   * Set `bypassLimits: true` for fixed-size content like:
   * - Static instructions or prompts
   * - Configuration that doesn't change
   * - Small, bounded metadata
   *
   * Keep `bypassLimits: false` (default) for variable-size content like:
   * - Query results, data tables, graphs
   * - User-generated content, logs
   * - Any content that could grow unexpectedly large
   *
   * @default false
   */
  bypassLimits?: boolean;
}

export interface NodeDataParams extends BaseParams {
  id: string;
  type: ItemDataType;
  text?: string;
}

export class NodeData {
  public id: string;
  public text = '';
  public type: ItemDataType;

  constructor(public params: NodeDataParams) {
    this.id = hash(params.id);
    this.type = params.type;
  }

  /** Whether size limits should be bypassed for this context item. Defaults to false. */
  get bypassLimits(): boolean {
    return this.params.bypassLimits ?? false;
  }

  formatForLLM(codeElementIds?: string[]): ContextItemData {
    return {
      type: this.type,
      codeElementIds,
      bypassLimits: this.bypassLimits,
      data: {
        name: this.params.text ?? '',
        text: this.params.text ?? '',
      } satisfies UnknownItemData,
    };
  }

  equals(other: unknown): boolean {
    if (!(other instanceof NodeData)) {
      return false;
    }
    return this.id === other.id && this.type === other.type;
  }
}

export interface StructuredNodeDataParams extends BaseParams {
  data: Record<string, any>;
}

export class StructuredNodeData extends NodeData {
  public data: Record<string, any>;
  constructor(params: StructuredNodeDataParams) {
    super({ ...params, id: hash(JSON.stringify(params.data)), type: 'structured' });
    this.data = params.data;
  }

  formatForLLM(codeElementIds?: string[]): ContextItemData {
    return {
      type: this.type,
      codeElementIds,
      bypassLimits: this.bypassLimits,
      data: this.data,
    };
  }
}
